<script lang="ts" module>
  export type Dimensions = {
    width: number
    height: number
    visibleRect: {
      x: number
      y: number
      width: number
      height: number
    }
  }
</script>

<script lang="ts">
  import { fit, getImageSourceDimensions, getVisibleRect, type ImageSource } from "@/libs/image"
  import { resize } from "@/libs/ui/svelte/actions"
  import { createVideo } from "@/libs/video/video"
  import { analytics } from "@/libs/telemetry"
  import { untrack } from "svelte"
  import type { HTMLAttributes } from "svelte/elements"

  let {
    src,
    isManual = false,
    mirrored = true,
    onStart,
    onStop,
    ...props
  }: {
    src: MediaStream
    isManual?: boolean
    mirrored?: boolean
    onStart?: () => void
    onStop?: () => void
  } & HTMLAttributes<HTMLDivElement> = $props()

  let container = $state<HTMLDivElement>()
  let canvas = $state<HTMLCanvasElement>()
  let video = $state<HTMLVideoElement>()
  let visibleRect = $state<ReturnType<typeof getVisibleRect> | null>(null)

  let height = $state(0)
  let width = $state(0)
  let isReady = $state(false)
  let isCanvasReady = $state(false)

  $effect(() => {
    if (!isManual) isCanvasReady = false
  })

  $effect(() => {
    visibleRect = null
    if (isReady) {
      analytics.photo_capture_opened()
      onStart?.()
    } else {
      onStop?.()
    }
  })

  export const getVideo = () => {
    if (!video) throw new Error("Couldn't get the video element: video is not available")

    if (!checkReady()) throw new Error("Couldn't get the video element: video is not ready")
    // spec-compliant https://www.w3.org/TR/webcodecs/#videoframe-constructors:~:text=If%20image%E2%80%99s%20networkState%20attribute%20is%20NETWORK_EMPTY
    if (video.networkState === video.NETWORK_EMPTY)
      throw new Error("Couldn't get the video element: video is not loaded")

    if (!src.active) throw new Error("Couldn't get the video element: stream is not active")

    return video
  }

  // spec-compliant https://www.w3.org/TR/webcodecs/#videoframe-constructors:~:text=VideoFrame(image%2C%20init)-,Check%20the%20usability%20of%20the%20image%20argument,-.%20If%20this%20throws
  export const checkReady = () =>
    Boolean(
      isReady &&
        video &&
        video.readyState !== video.HAVE_NOTHING &&
        video.readyState !== video.HAVE_METADATA,
    )

  const getVideoVisibleRect = () => visibleRect || (visibleRect = getVisibleRect(getVideo()))
  let ctx: CanvasRenderingContext2D | undefined | null = null
  /**
   * Captures current frame to draw it on canvas later. Feasible when you need to first process the frame and then draw the result using this frame (for example, to synchronize frames with calculations).
   *
   * The frame is captured only once, so you have to call this function every time you need a new frame.
   *
   * Returns `null` when `VideoFeed`s `isManual` prop is `false`.
   *
   * Example:
   *
   * ```javascript
   * const capturedFrame = await video.captureFrame()
   *
   * ...frame processing...
   *
   * capturedFrame?.draw()
   * ```
   */
  export const captureCurrentFrame = async () => {
    if (!isManual) return null

    const frame = await createImageBitmap(getVideo())

    return {
      draw() {
        if (!isManual) return
        if (!ctx && !(ctx = canvas?.getContext("2d"))) return

        const rect = getVideoVisibleRect()

        ctx.drawImage(frame, rect.x, rect.y, rect.width, rect.height, 0, 0, width, height)
        frame.close()

        if (!isCanvasReady) isCanvasReady = true
      },
    }
  }
  export const getDimensions = (imageSource?: ImageSource): Dimensions => ({
    /** Visible part of the frame pixels after rendering */
    visibleRect: getVideoVisibleRect(),
    /** Frame width and height in pixels */
    ...getImageSourceDimensions(imageSource ?? getVideo()),
  })
  export const getFrame = async () => {
    const frame = await createImageBitmap(getVideo())

    return { frame, dimensions: getDimensions(frame) }
  }

  const handleResize = (el: HTMLElement, entry?: ResizeObserverEntry) => {
    visibleRect = null
    width = entry?.contentRect.width ?? el.offsetWidth
    height = entry?.contentRect.height ?? el.offsetHeight
  }

  $effect(() => {
    untrack(() => video?.remove())

    visibleRect = null
    video = createVideo(src)

    untrack(() => {
      video && container?.appendChild(video)
    })
  })

  $effect(() => {
    if (!video) return

    const abortController = new AbortController()

    video.addEventListener("play", () => (isReady = true), {
      signal: abortController.signal,
    })
    video.addEventListener("ended", () => (isReady = false), {
      signal: abortController.signal,
    })

    return () => abortController?.abort()
  })
</script>

<div
  {...props}
  class={["video-feed-container", "relative size-full bg-[#16161d]", props.class]}
  class:-scale-x-100={mirrored}
  class:manual={isManual && isCanvasReady}
  use:resize={handleResize}
  bind:this={container}
>
  {#if isManual}
    {@const dimensions = fit({ width, height }, 1080)}

    <canvas
      class="absolute inset-0"
      height={dimensions.height}
      width={dimensions.height}
      bind:this={canvas}
    ></canvas>
  {/if}
</div>

<style lang="postcss">
  .video-feed-container > :global(video) {
    @apply size-full object-cover;
  }
  .manual > :global(video) {
    @apply invisible;
  }
</style>
